import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { api } from "@/lib/api";
import { fmtCurrencyExact, fmtCurrency, fmtDate, FENCE_LABELS } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Printer, ArrowLeft } from "lucide-react";

export default function QuotePrint() {
  const { id } = useParams();
  const { data: quote } = useQuery({ queryKey: ["quote-print", id], queryFn: async () => (await api.get(`/quotes/${id}`)).data });

  useEffect(() => { document.title = quote?.quote_number ? `${quote.quote_number} — CCFI` : "Quote"; }, [quote?.quote_number]);

  if (!quote) return <div className="p-10 text-slate-500">Loading…</div>;

  return (
    <div className="min-h-screen bg-slate-100 py-8">
      <div className="max-w-[820px] mx-auto px-4">
        <div className="flex items-center justify-between mb-4 no-print">
          <Button variant="outline" onClick={() => window.history.back()} className="rounded-sm"><ArrowLeft size={14} className="mr-1.5" /> Back</Button>
          <Button data-testid="print-quote-btn" onClick={() => window.print()} className="rounded-sm bg-cyan-600 hover:bg-cyan-700"><Printer size={14} className="mr-1.5" /> Print / Save PDF</Button>
        </div>

        <div className="bg-white industrial-shadow-lg p-12 print:p-0 print:shadow-none">
          {/* Header */}
          <div className="flex items-start justify-between border-b border-slate-900 pb-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <img src="/ccfi-logo.png?v=4" alt="CCFI" className="w-14 h-14 object-contain" />
                <div>
                  <div className="font-display text-lg font-semibold text-slate-900">Central Coast Fencing Industries</div>
                  <div className="text-xs text-slate-500">Fencing · Steel Fabrication · Central Coast NSW</div>
                </div>
              </div>
              <div className="text-xs text-slate-600 leading-relaxed">
                8 Dignity Cres, West Gosford NSW 2250<br />
                02 4324 1441 · info@ccfencingindustries.com.au<br />
                centralcoastfencingindustries.com.au
              </div>
            </div>
            <div className="text-right">
              <div className="stamp-label text-cyan-600">QUOTE</div>
              <div className="font-display text-3xl font-bold text-slate-900 mt-1">{quote.quote_number}</div>
              <div className="text-xs text-slate-500 mt-1">Issued {fmtDate(quote.created_at)}</div>
              <div className="text-xs text-slate-500">Status: <span className="font-semibold uppercase">{quote.status}</span></div>
            </div>
          </div>

          {/* Customer + project */}
          <div className="grid grid-cols-2 gap-8 mt-8">
            <div>
              <div className="stamp-label text-slate-500 mb-2">Customer</div>
              <div className="font-display text-lg font-semibold text-slate-900">{quote.customer_name}</div>
              {quote.customer_email && <div className="text-sm text-slate-600">{quote.customer_email}</div>}
              {quote.customer_phone && <div className="text-sm text-slate-600">{quote.customer_phone}</div>}
              {quote.site_address && <div className="text-sm text-slate-600 mt-1">{quote.site_address}</div>}
            </div>
            <div>
              <div className="stamp-label text-slate-500 mb-2">Project</div>
              <div className="text-sm">
                <div><span className="text-slate-500">Type:</span> <span className="font-semibold text-slate-900">{FENCE_LABELS[quote.fence_type] || quote.fence_type}</span></div>
                {quote.linear_meters > 0 && <div><span className="text-slate-500">Linear meters:</span> <span className="font-semibold text-slate-900">{quote.linear_meters} m</span></div>}
                {quote.square_meters > 0 && <div><span className="text-slate-500">Square meters:</span> <span className="font-semibold text-slate-900">{quote.square_meters} m²</span></div>}
              </div>
            </div>
          </div>

          {/* Scope */}
          {quote.scope_summary && (
            <div className="mt-8 border border-slate-200 p-4 bg-slate-50/40">
              <div className="stamp-label text-slate-500 mb-2">Scope of works</div>
              <p className="text-sm text-slate-800 leading-relaxed whitespace-pre-line">{quote.scope_summary}</p>
            </div>
          )}

          {/* Line items */}
          <div className="mt-8">
            <div className="stamp-label text-slate-500 mb-3">Line items</div>
            <table className="w-full text-sm border-t border-slate-300">
              <thead>
                <tr className="text-left stamp-label text-slate-500 border-b border-slate-300">
                  <th className="py-2 w-12">#</th>
                  <th className="py-2">Description</th>
                  <th className="py-2 text-right w-16">Qty</th>
                  <th className="py-2 w-14">Unit</th>
                  <th className="py-2 text-right w-24">Unit $</th>
                  <th className="py-2 text-right w-28">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {(quote.line_items || []).map((it, idx) => (
                  <tr key={it.id || idx}>
                    <td className="py-2 font-mono text-xs text-slate-500">{idx + 1}</td>
                    <td className="py-2 text-slate-900">{it.name}</td>
                    <td className="py-2 text-right font-mono">{it.qty}</td>
                    <td className="py-2 text-slate-600 text-xs">{it.unit}</td>
                    <td className="py-2 text-right font-mono">{fmtCurrencyExact(it.unit_price)}</td>
                    <td className="py-2 text-right font-mono font-semibold">{fmtCurrencyExact((it.qty || 0) * (it.unit_price || 0))}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totals */}
          <div className="mt-8 flex justify-end">
            <div className="w-80 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-slate-600">Subtotal (cost)</span><span className="font-mono">{fmtCurrencyExact(quote.total_cost)}</span></div>
              <div className="flex justify-between"><span className="text-slate-600">GP margin ({quote.gp_margin_percent}%)</span><span className="font-mono">{fmtCurrencyExact(quote.gp_dollars)}</span></div>
              <div className="border-t border-slate-300 pt-2 flex justify-between items-baseline">
                <span className="font-display font-semibold text-slate-900">Total (AUD ex GST)</span>
                <span className="font-display text-2xl font-bold text-cyan-600">{fmtCurrency(quote.sale_price)}</span>
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="mt-12 pt-6 border-t border-slate-200 text-xs text-slate-500 leading-relaxed">
            <p><span className="stamp-label text-slate-600">Terms:</span> This quote is valid for 30 days from issue. Payment 50% on acceptance, balance on completion. All work warranted for 12 months. GST not included.</p>
            <p className="mt-3">Thank you for the opportunity to quote.</p>
          </div>
        </div>
      </div>
    </div>
  );
}
